import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../css/blockVolunteerList.module.scss";
import { BLOCK_VOLUNTEER_PATHS, PRE_BLOCK_VOLUNTEER_PATHS } from "../../../../Routes/paths";
import { useGlobalStudy } from "../../../../context/StudyContext";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";
import StatCard from "../../../../Components/Common/StatCard";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import { blockVolunteerColumns } from "../helper/blockvolunteer.constant";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "../../../../common/constants/PermissionConstants";
import { useBlockVolunteerService } from "../api/useBlockVolunteerQuery";
import { perPageData, GetUserData, showToastMessage } from "../../../../common/service/CommonService";
import { queryClient } from "api/queryClient";
import PinVerificationModal from "../../../Pharmacy/DailyReconsilation/components/PinVerificationModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import CommonButton from "../../../../Components/Common/CommonButton";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";


const BlockVolunteerList: React.FC = () => {
  // -------------------------------------------------------------------------------------------
  // 1. Hooks & Context
  // -------------------------------------------------------------------------------------------
  document.title = "Block Volunteer | ZENTIXS VISTA";
  const navigate = useNavigate();
  const { selectedStudy: globalStudy } = useGlobalStudy();
  const userData = GetUserData();

  // -------------------------------------------------------------------------------------------
  // 2. States
  // -------------------------------------------------------------------------------------------
  // Filters & Pagination
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setcurrentPageNumber] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<"all" | "pre-assignment" | "post-enrollment" | "ambulatory-visit">("pre-assignment");

  const handleTabChange = (tab: "all" | "pre-assignment" | "post-enrollment" | "ambulatory-visit") => {
    setActiveTab(tab);
    setcurrentPageNumber(1);
    setSearchText("");
  };

  // Selected Record
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  // Modals
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  // Audit States
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  // -------------------------------------------------------------------------------------------
  // 3. Effects
  // -------------------------------------------------------------------------------------------
  document.title = "Subject Blocking Management | ZENTIXS VISTA";

  const prevStudyRef = useRef(globalStudy?.studyid);
  useEffect(() => {
    if (prevStudyRef.current !== globalStudy?.studyid) {
      prevStudyRef.current = globalStudy?.studyid;
      setcurrentPageNumber(1);
    }
  }, [globalStudy?.studyid]);

  // -------------------------------------------------------------------------------------------
  // 4. API Queries & Mutations
  // -------------------------------------------------------------------------------------------
  const { SelectAllQuery, VerifyMutation, DeleteMutation } = useBlockVolunteerService({
    searchText: searchText,
    studyid: activeTab === "pre-assignment" ? null : Number(globalStudy?.studyid),
    currentPage: currentPageNumber,
    pageSize: perPageData,
    enableSelectAll: !!globalStudy?.studyid,
    ...(activeTab === "all"
      ? {}
      : {
          blocksource: activeTab === "ambulatory-visit" ? "ambulatory-visit" : activeTab,
        }),
  });

  const { SelectAllMutation: AuditMutation } = useAuditService();

  const rawData = SelectAllQuery?.data?.data || [];
  const apiRecords = rawData.filter((r: any) => !r.summarycard);
  const summaryData = rawData.find((r: any) => r.summarycard)?.summarycard;
  const totalRecords = apiRecords[0]?.total_count || 0;
  const isLoading = SelectAllQuery?.isLoading || SelectAllQuery?.isFetching;

  // -------------------------------------------------------------------------------------------
  // 5. Data Processing
  // -------------------------------------------------------------------------------------------
  const totalBlocked = summaryData?.totalblocked;
  const tempBlocked = summaryData?.temporaryblocks;
  const permBlocked = summaryData?.permanentblocks;

  // Hide "Study Code / Subject No" column on Pre-Assignment tab
  const visibleColumns = activeTab === "pre-assignment"
    ? blockVolunteerColumns.filter((col) => col.key !== "subjectno")
    : blockVolunteerColumns;

  // -------------------------------------------------------------------------------------------
  // 6. Handlers
  // -------------------------------------------------------------------------------------------

  const toggleDeleteModal = () => {
    setDeleteModal((p) => !p);
    setShowDeleteReason(false);
  };
  const toggleDeleteReason = () => setShowDeleteReason((p) => !p);

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setcurrentPageNumber(1);
  };

  const handleVerifyClick = (item: any) => {
    setSelectedRecord(item);
    setVerifyModal(true);
  };

  const handlePinVerify = (password: string) => {
    const payload = {
      verifypin: password,
      blockid: Number(selectedRecord?.blockid || selectedRecord?.id),
      verifiedby: Number(userData?.userid),
    };

    VerifyMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["block-volunteer-select-all"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message);
        setVerifyModal(false);
        setSelectedRecord(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  const handleDeleteClick = (item: any) => {
    setSelectedRecord(item);
    toggleDeleteModal();
  };

  const confirmDelete = (reasonPayload: { id: number | null; reason: string }) => {
    if (!selectedRecord) return;

    DeleteMutation.mutate(
      {
        blockid: selectedRecord.blockid || selectedRecord.id,
        updatereason: reasonPayload.reason,
      },
      {
        onSuccess: (res: any) => {
          queryClient.invalidateQueries({ queryKey: ["block-volunteer-select-all"] });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          showToastMessage("success", res?.message || "Record deleted successfully");
          setSelectedRecord(null);
          toggleDeleteModal();
          toggleDeleteReason();
        },
        onError: (err: any) => {
          showToastMessage("error", err?.message ?? err ?? "Failed to delete record");
        },
      }
    );
  };

  const getRowActions = (row: any): any => {
    const isVerified = row?.isverified;
    const actions: any = {};

    if (!isVerified) {
      actions.onVerify = (r: any) => handleVerifyClick(r);
      actions.onEdit = (r: any) => activeTab === "pre-assignment" ? navigate(PRE_BLOCK_VOLUNTEER_PATHS.EDIT.replace(":id", r.blockid.toString())) : navigate(BLOCK_VOLUNTEER_PATHS.EDIT.replace(":id", r.blockid.toString()));
      actions.onDelete = (r: any) => handleDeleteClick(r);
    }
    return actions;
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Volunteer Block History",
      studyid: Number(globalStudy?.studyid)
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Volunteer_Block_History_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  // -------------------------------------------------------------------------------------------
  // 7. Render
  // -------------------------------------------------------------------------------------------


  return (
    <div className="page-content">
      <Container fluid className="pb-4">
        {!globalStudy?.studyid ? (
          <div>
            <NoStudySelectedPlaceholder moduleName="block volunteer list" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mt-1 mb-3 px-2">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>Subject Blocking Management</h5>
                </div>
                <p className={styles.pageHeaderSubtitle}>
                  Manage temporary and permanent exclusions of volunteers from study participation.
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.BLOCKED_VOLUNTEER}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600"
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
              </div>
            </div>

            {/* Stat Cards */}
            <Row className="g-3 mb-3 px-1">
              <Col md={4}>
                <StatCard
                  loading={isLoading}
                  title="TOTAL BLOCKED"
                  value={totalBlocked}
                  subtext="Across all studies"
                  icon={<i className="ri-shield-user-line" />}
                  bgColor="#fee2e2"
                  iconColor="#dc2626"
                  trendType="info"
                  subtextIcon="ri-stack-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  loading={isLoading}
                  title="TEMPORARY BLOCKS"
                  value={tempBlocked}
                  subtext="Active duration blocks"
                  icon={<i className="ri-time-line" />}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  trendType="negative"
                  subtextIcon="ri-error-warning-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  loading={isLoading}
                  title="PERMANENT BLOCKS"
                  value={permBlocked}
                  subtext="Lifetime exclusions"
                  icon={<i className="ri-prohibited-line" />}
                  bgColor="#fee2e2"
                  iconColor="#dc2626"
                  trendType="negative"
                  subtextIcon="ri-close-circle-line"
                />
              </Col>
            </Row>

            {/* Table List Panel */}
            <div className={styles.listPanel}>
              <div className={styles.panelHeader}>
                <div className="d-flex align-items-center gap-3">
                  <h2>
                    <i className="ri-user-forbid-line me-2 text-primary fs-18" />
                    Blocking Records
                  </h2>
                </div>

                {/* Tab Switcher */}
                <div className="p-1 bg-light rounded-pill d-inline-flex border">
                  <button
                    type="button"
                    className={`btn btn-sm px-3 rounded-pill fw-semibold border-0 ${activeTab === "all" ? "text-primary shadow-sm" : "text-muted"
                      }`}
                    style={{
                      fontSize: "12px",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                      backgroundColor: activeTab === "all" ? "#ffffff" : "transparent",
                    }}
                    onClick={() => handleTabChange("all")}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm px-3 rounded-pill fw-semibold border-0 ${activeTab === "pre-assignment" ? "text-primary shadow-sm" : "text-muted"
                      }`}
                    style={{
                      fontSize: "12px",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                      backgroundColor: activeTab === "pre-assignment" ? "#ffffff" : "transparent",
                    }}
                    onClick={() => handleTabChange("pre-assignment")}
                  >
                    Pre-Assignment
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm px-3 rounded-pill fw-semibold border-0 ${activeTab === "post-enrollment" ? "text-primary shadow-sm" : "text-muted"
                      }`}
                    style={{
                      fontSize: "12px",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                      backgroundColor: activeTab === "post-enrollment" ? "#ffffff" : "transparent",
                    }}
                    onClick={() => handleTabChange("post-enrollment")}
                  >
                    Post-Enrollment
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm px-3 rounded-pill fw-semibold border-0 ${activeTab === "ambulatory-visit" ? "text-primary shadow-sm" : "text-muted"
                      }`}
                    style={{
                      fontSize: "12px",
                      paddingTop: "2px",
                      paddingBottom: "2px",
                      backgroundColor: activeTab === "ambulatory-visit" ? "#ffffff" : "transparent",
                    }}
                    onClick={() => handleTabChange("ambulatory-visit")}
                  >
                    Ambulatory-Visit
                  </button>
                </div>

                <CommonSearch onSearch={onSearch} searchText={searchText} />
              </div>

              <div className="px-4 py-2">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.BLOCKED_VOLUNTEER}
                  columns={visibleColumns}
                  data={apiRecords}
                  loading={isLoading}
                  pageSize={perPageData}
                  currentPage={currentPageNumber}
                  totalRecords={totalRecords}
                  onPageChange={setcurrentPageNumber}
                  actions={getRowActions}
                />
              </div>
            </div>
          </>
        )}
        <PinVerificationModal
          isOpen={verifyModal}
          toggle={() => setVerifyModal(false)}
          onVerify={handlePinVerify}
          isLoading={VerifyMutation.isPending}
          title="Verify Block Record"
          description={
            <>
              Please enter your password to verify block record for volunteer:{" "}
              <strong>{selectedRecord?.initials}</strong>
            </>
          }
          isInvalid={VerifyMutation.isError}
        />
        {/* Delete Modal */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={toggleDeleteModal}
            recordId={selectedRecord?.blockid || selectedRecord?.id}
            recordName={selectedRecord?.volunteername || selectedRecord?.initials}
            onDeleteClick={confirmDelete}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
            isLoading={DeleteMutation.isPending}
          />
        )}
        {/* File Preview Modal */}
        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size={"xl"}
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}
      </Container>
    </div>
  );
};

export default BlockVolunteerList;
